import { yupResolver } from '@hookform/resolvers/yup';
import LockOutlined from '@mui/icons-material/LockOutlined';
import {
  ChangePasswordPageSchema,
  ConfirmPasswordField,
  PasswordField,
  UserEmailFields,
} from '@src/authentication/ChangePasswordFields';
import { Button } from '@src/common/components/button/Button';
import { Header } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { colors } from '@src/common/styles/valueTokens';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { Navigation } from '@src/routing/Navigation';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { InferType } from 'yup';

import { useChangePassword } from '../useChangePassword';

const fieldsWidth = { xs: '272px', md: '310px' };

export const ChangeTemporaryPasswordPage = () => {
  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = useToast();
  const { t } = useTranslation();
  const [searchParam] = useSearchParams();

  const { changePassword, isLoading } = useChangePassword({
    onSuccess: () => {
      showSuccessToast(t('changeTemporaryPassword.success'));
      navigate(Navigation.login);
    },
    onError: () => {
      showErrorToast(t('changeTemporaryPassword.error'));
    },
  });

  const { handleSubmit, control } = useForm({
    resolver: yupResolver(ChangePasswordPageSchema),
    defaultValues: {
      [UserEmailFields.fieldName]: searchParam.get('email') ?? '',
    },
  });

  const handleChangePassword: SubmitHandler<
    InferType<typeof ChangePasswordPageSchema>
  > = async (data) => {
    changePassword({
      userEmail: data[UserEmailFields.fieldName],
      newPassword: data[PasswordField.fieldName],
    });
  };

  return (
    <Stack className={page}>
      <Stack width={fieldsWidth}>
        <form onSubmit={handleSubmit(handleChangePassword)}>
          <Stack gap={theme.spacing['4']} marginBottom={theme.spacing['6']}>
            <LockOutlined fontSize="large" />

            <Header title={t('changeTemporaryPassword.title')} />

            <Typography style={{ color: colors.text.secondary }}>
              {t('changeTemporaryPassword.subtitle1')}
            </Typography>

            <Typography style={{ color: colors.text.secondary }}>
              {t('changeTemporaryPassword.subtitle2')}
            </Typography>
          </Stack>

          <Stack gap={theme.spacing['4']} width={fieldsWidth}>
            <ControllerFormTextInput
              control={control}
              disabled={!!searchParam.get('email')}
              fieldName={UserEmailFields.fieldName}
              autoComplete="new-email"
            />
            <ControllerFormTextInput
              control={control}
              disabled={isLoading}
              fieldName={PasswordField.fieldName}
              type="password"
              autoComplete="new-password"
              label={t('newPassword')}
            />
            <ControllerFormTextInput
              control={control}
              disabled={isLoading}
              fieldName={ConfirmPasswordField.fieldName}
              type="password"
              autoComplete="confirm-new-password"
              label={t('confirmPassword')}
            />
          </Stack>

          <Stack marginTop={theme.spacing['12']}>
            <Button variant="contained" type="submit">
              {t('submit')}
            </Button>
          </Stack>
        </form>
      </Stack>
    </Stack>
  );
};

const page = css(() => ({
  height: '100vh',
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));
